import moment, { type Moment } from 'moment'

export interface ChatActivityTimestampRecord {
  createdAt: string
  updatedAt?: string
}

export const getChatLastActivityTimestamp = (chat: ChatActivityTimestampRecord): string => {
  return chat.updatedAt ?? chat.createdAt
}

export type SortableTimestampValue = string | { valueOf: () => number } | undefined

export function getTimestampSortValue(timestamp: SortableTimestampValue): number {
  if (!timestamp) return 0

  const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp.valueOf()
  return Number.isNaN(time) ? 0 : time
}

export type ChatActivityAge =
  | { unit: 'minutes', count: number }
  | { unit: 'hours', count: number }
  | { unit: 'days', count: number }
  | { unit: 'date', sameYear: boolean }
  | { unit: 'invalid' }

const MAX_RELATIVE_DAYS = 7

/** Elapsed minutes and hours within the last day, then calendar days up to a week, then a date. */
export function getChatActivityAge(updatedAt: string, now: Moment): ChatActivityAge {
  const updatedAtMoment = moment(updatedAt)
  if (!updatedAtMoment.isValid()) {
    return { unit: 'invalid' }
  }

  // A server timestamp ahead of a slow client clock gives a negative diff, which floors to 1m.
  const elapsedMinutes = now.diff(updatedAtMoment, 'minutes')
  if (elapsedMinutes < 60) {
    return { unit: 'minutes', count: Math.max(1, elapsedMinutes) }
  }

  const elapsedHours = now.diff(updatedAtMoment, 'hours')
  if (elapsedHours < 24) {
    return { unit: 'hours', count: elapsedHours }
  }

  const calendarDays = now.clone().startOf('day').diff(updatedAtMoment.clone().startOf('day'), 'days')
  if (calendarDays <= MAX_RELATIVE_DAYS) {
    return { unit: 'days', count: calendarDays }
  }

  return { unit: 'date', sameYear: updatedAtMoment.year() === now.year() }
}
